/**
 * @file SpiffProgramWorkspace.jsx
 * @module ui/src/components/admin/SpiffProgramWorkspace.jsx
 * @description
 *   C-66 — SPIFF Program Workspace. Runtime instrument surface.
 *   EXECUTE → Programs & Adjustments → SPIFF Programs.
 *
 *   6 tabs: Overview · Rules · Population · Awards · Approval · Activity
 *
 *   S924 — New component. Reference implementation for all runtime instrument
 *   workspaces (Contest, Bonus Pool, Non-Cash follow same shell with
 *   instrument-specific fields).
 *
 *   Architecture:
 *   - Standalone runtime instrument — does NOT use usePlanDocument,
 *     usePlanGovernance, or usePlanValidation (design-time hooks).
 *   - Uses useSpiffProgram hook for all API interactions.
 *   - ProgramPopulationPanel (C-67), ProgramApprovalPanel (C-68),
 *     ProgramActivityPanel (C-69) are inline in this file per
 *     OI-S813-MODIFIER-SUBCOMPONENT-FILES pattern (refactor when >150L each).
 *
 *   CSS: index.css + tokens.css. All colours via rgb(var(--color-*)).
 *   Button classes: .primary-button, .secondary-button, .icon-button, .text-button
 *   Table classes: .modifier-manager-table-wrapper, .modifier-manager-table
 *   Badge classes: .modifier-manager-badge--*
 *
 *   UI directives (S924):
 *   D-1: Tables that grow long → scrollable + sortable + searchable + clickable
 *   D-2: Long screens → collapsible sections (secondary collapsed by default)
 *   D-3: Population filter bar → BU, Segment, Selling Motion, Role (minimum)
 *
 * @author data-fit LLC · DecisionFlow · S924
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import useSpiffProgram from '../../hooks/useSpiffProgram';

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'overview',    label: 'Overview' },
  { key: 'rules',       label: 'Rules' },
  { key: 'population',  label: 'Population' },
  { key: 'awards',      label: 'Awards' },
  { key: 'approval',    label: 'Approval' },
  { key: 'activity',    label: 'Activity' },
];

const STATUS_BADGE = {
  draft:           'modifier-manager-badge--not-created',
  pending_approval:'modifier-manager-badge--warning',
  approved:        'modifier-manager-badge--complete',
  superseded:      'modifier-manager-badge--readonly',
};

const STATUS_LABEL = {
  draft:            'Draft',
  pending_approval: 'Awaiting Approval',
  approved:         'Approved',
  superseded:       'Superseded',
};

const TRIGGER_LABELS = {
  transaction_triggered: 'Transaction Triggered',
  manager_assigned:      'Manager Assigned',
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function _iso(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

function _fmt(amount, currency) {
  if (amount == null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return `${currency || ''} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
}

// ---------------------------------------------------------------------------
// Inline field component
// ---------------------------------------------------------------------------
function Field({ label, hint, error, children, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>
          {label}{required && <span style={{ color: 'rgb(var(--color-danger))' }}> *</span>}
        </label>
      )}
      {children}
      {hint && !error && <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', marginTop: 2 }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: 'rgb(var(--color-danger))', marginTop: 2 }} role="alert">{error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible section wrapper (D-2)
// ---------------------------------------------------------------------------
function CollapsibleSection({ title, defaultOpen = true, children, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      border: '1px solid rgb(var(--color-border))',
      borderRadius: 10,
      background: 'rgb(var(--color-surface))',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '12px 16px',
          background: 'none', border: 'none', cursor: 'pointer',
          borderBottom: open ? '1px solid rgb(var(--color-border))' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
          {badge && <span className={`modifier-manager-badge ${badge.cls}`}>{badge.label}</span>}
        </div>
        <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only info row
// ---------------------------------------------------------------------------
function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'rgb(var(--color-text-muted))', minWidth: 180 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{value ?? '—'}</span>
    </div>
  );
}

// ===========================================================================
// TAB 1 — OVERVIEW
// ===========================================================================
function OverviewTab({ program }) {
  if (!program) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Program summary — always visible */}
      <CollapsibleSection title="Program Summary" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Program name"    value={program.planName} />
          <InfoRow label="Program code"    value={program.spiffPlanCode} />
          <InfoRow label="Trigger model"   value={TRIGGER_LABELS[program.triggerModel] ?? program.triggerModel} />
          <InfoRow label="Effective from"  value={program.effectiveFrom} />
          <InfoRow label="Effective to"    value={program.effectiveTo ?? 'Open ended'} />
          <InfoRow label="Payout currency" value={program.payoutCurrency} />
          <InfoRow label="Payout cap"      value={program.payoutCap ? _fmt(program.payoutCap, program.payoutCurrency) : 'None'} />
          <InfoRow label="Version"         value={`V${program.version}`} />
          <InfoRow label="Created"         value={_iso(program.createdAt)} />
        </div>
      </CollapsibleSection>

      {/* Policy envelope — expanded by default (primary info) */}
      <CollapsibleSection title="Policy Envelope" defaultOpen
        badge={program.maxIndividualAward || program.financeApprovalThreshold
          ? { cls: 'modifier-manager-badge--complete', label: 'Configured' }
          : { cls: 'modifier-manager-badge--not-created', label: 'No caps set' }}
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
          Inherited from governing plan. Read-only — changes require a plan amendment.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Max individual award"      value={program.maxIndividualAward ? _fmt(program.maxIndividualAward, program.payoutCurrency) : 'No cap'} />
          <InfoRow label="Max duration"              value={program.maxDurationDays ? `${program.maxDurationDays} days` : 'No limit'} />
          <InfoRow label="Finance gate threshold"    value={program.financeApprovalThreshold ? _fmt(program.financeApprovalThreshold, program.payoutCurrency) : 'Not required'} />
          <InfoRow label="Permitted payout forms"    value={program.permittedPayoutForms?.length ? program.permittedPayoutForms.join(', ') : 'All forms permitted'} />
        </div>
      </CollapsibleSection>

      {/* Population summary */}
      <CollapsibleSection title="Population" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Assigned positions" value={program.activeAssignmentCount?.toLocaleString() ?? '0'} />
          <InfoRow label="Snapshot status"    value={program.activeAssignmentCount > 0 ? 'Locked' : 'Not yet committed'} />
        </div>
      </CollapsibleSection>

      {/* Engine gate — collapsed by default (secondary) */}
      <CollapsibleSection title="Engine Gate Status" defaultOpen={false}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="G-33 gate"
            value={program.approvedBy
              ? '✓ SATISFIED — approved_by IS NOT NULL. Next calculation run will include this program.'
              : '⏳ PENDING — approve this program for engine inclusion.'} />
          <InfoRow label="Submitted by"  value={program.submittedBy ?? '—'} />
          <InfoRow label="Submitted at"  value={_iso(program.submittedAt)} />
          <InfoRow label="Approved by"   value={program.approvedBy ?? '—'} />
          <InfoRow label="Approved at"   value={_iso(program.approvedAt)} />
        </div>
      </CollapsibleSection>

    </div>
  );
}

// ===========================================================================
// TAB 2 — RULES
// ===========================================================================
function RulesTab({ program, planId, readOnly, onRulesChanged }) {
  const [tiers, setTiers]           = useState([]);
  const [ruleName, setRuleName]     = useState('');
  const [productLine, setProductLine] = useState('');
  const [minDealAmount, setMinDealAmount] = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);

  // Populate from existing rule (first rule only — Phase 1 = 1 rule per plan)
  const existingRule = program?.rules?.[0] ?? null;

  useEffect(() => {
    if (existingRule) {
      setRuleName(existingRule.rule_name ?? '');
      setProductLine(existingRule.product_line ?? '');
      setMinDealAmount(existingRule.min_deal_amount ?? '');
      const ts = existingRule.tier_schedule_json;
      if (Array.isArray(ts)) setTiers(ts);
      else if (ts?.tiers) setTiers(ts.tiers);
    }
  }, [existingRule?.rule_id]);

  const addTier = () => setTiers(t => [...t, { minQty: '', maxQty: '', payoutAmount: '' }]);
  const removeTier = (i) => setTiers(t => t.filter((_, idx) => idx !== i));
  const updateTier = (i, field, val) =>
    setTiers(t => t.map((tier, idx) => idx === i ? { ...tier, [field]: val } : tier));

  const isManagerAssigned = program?.triggerModel === 'manager_assigned';
  const isDraft = program?.planStatus === 'draft';
  const canEdit = isDraft && !readOnly;

  async function saveRule() {
    if (!planId) return;
    setSaving(true); setError(null);
    try {
      const body = {
        rule_name: ruleName,
        product_line: productLine || null,
        min_deal_amount: minDealAmount || null,
        tier_schedule_json: { tiers },
      };
      const url = existingRule
        ? `${API_BASE}/spiff-plans/${planId}/rules/${existingRule.rule_id}`
        : `${API_BASE}/spiff-plans/${planId}/rules`;
      const method = existingRule ? 'PATCH' : 'POST';
      const r = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b?.error?.message || `Failed to save rule (${r.status})`);
      }
      onRulesChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {!canEdit && (
        <div className="modifier-manager-readonly-note">
          Rules are read-only — plan is {program?.planStatus}. {isDraft ? '' : 'Withdraw submission or supersede to edit.'}
        </div>
      )}

      {/* Program fields */}
      <CollapsibleSection title="Program Definition" defaultOpen>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <Field label="Rule name" required>
            <input
              style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
              value={ruleName}
              onChange={e => setRuleName(e.target.value)}
              placeholder="e.g. Q3 Cloud Upsell Rule"
              disabled={!canEdit}
            />
          </Field>
          <Field label="Trigger model">
            <input
              style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface-muted))', color: 'rgb(var(--color-text-muted))', width: '100%', boxSizing: 'border-box' }}
              value={TRIGGER_LABELS[program?.triggerModel] ?? ''}
              disabled
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* Tier schedule */}
      {isManagerAssigned ? (
        <div className="modifier-manager-empty-state">
          <h4>Manager Assigned</h4>
          <p>Award amounts are set by the manager at time of award. No tier schedule required for this trigger model.</p>
        </div>
      ) : (
        <CollapsibleSection title="Tier Schedule" defaultOpen>

          {/* Qualification scope — collapsed by default */}
          <CollapsibleSection title="Qualification Scope (Optional)" defaultOpen={false}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <Field label="Product line" hint="Leave blank to apply to all products">
                <input
                  style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: canEdit ? 'rgb(var(--color-surface))' : 'rgb(var(--color-surface-muted))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
                  value={productLine}
                  onChange={e => setProductLine(e.target.value)}
                  placeholder="e.g. Security (optional)"
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Min deal amount" hint="Only transactions above this amount qualify">
                <input
                  type="number"
                  style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: canEdit ? 'rgb(var(--color-surface))' : 'rgb(var(--color-surface-muted))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
                  value={minDealAmount}
                  onChange={e => setMinDealAmount(e.target.value)}
                  placeholder="Optional"
                  disabled={!canEdit}
                />
              </Field>
            </div>
          </CollapsibleSection>

          {/* Tier rows table */}
          <div style={{ marginTop: 12 }}>
            {tiers.length === 0 ? (
              <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', marginBottom: 8 }}>
                No tiers defined. At least one tier is required for transaction-triggered plans.
              </div>
            ) : (
              <div className="modifier-manager-table-wrapper" style={{ marginBottom: 8 }}>
                <table className="modifier-manager-table">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>#</th>
                      <th>Min qty *</th>
                      <th>Max qty</th>
                      <th>Payout amount *</th>
                      {canEdit && <th style={{ width: 40 }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((tier, i) => (
                      <tr key={i}>
                        <td className="modifier-manager-band-number">{i + 1}</td>
                        <td>
                          <input type="number" className="modifier-manager-percent-input"
                            style={{ width: '100%', padding: '4px 8px', border: '1px solid rgb(var(--color-border))', borderRadius: 6, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', fontSize: 12 }}
                            value={tier.minQty ?? ''} min="0"
                            onChange={e => updateTier(i, 'minQty', e.target.value)}
                            placeholder="e.g. 1" disabled={!canEdit}
                            aria-label={`Tier ${i+1} min qty`}
                          />
                        </td>
                        <td>
                          <input type="number"
                            style={{ width: '100%', padding: '4px 8px', border: '1px solid rgb(var(--color-border))', borderRadius: 6, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', fontSize: 12 }}
                            value={tier.maxQty ?? ''} min="0"
                            onChange={e => updateTier(i, 'maxQty', e.target.value || null)}
                            placeholder="blank = open" disabled={!canEdit}
                            aria-label={`Tier ${i+1} max qty`}
                          />
                        </td>
                        <td>
                          <input type="number"
                            style={{ width: '100%', padding: '4px 8px', border: '1px solid rgb(var(--color-border))', borderRadius: 6, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', fontSize: 12 }}
                            value={tier.payoutAmount ?? ''} min="0" step="0.01"
                            onChange={e => updateTier(i, 'payoutAmount', e.target.value)}
                            placeholder="e.g. 500" disabled={!canEdit}
                            aria-label={`Tier ${i+1} payout`}
                          />
                        </td>
                        {canEdit && (
                          <td>
                            <button type="button" className="icon-button text-button--danger"
                              onClick={() => removeTier(i)}
                              aria-label={`Remove tier ${i+1}`}
                              style={{ color: 'rgb(var(--color-danger))' }}
                            >×</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {canEdit && (
              <button type="button" className="secondary-button" onClick={addTier}>
                + Add tier
              </button>
            )}
          </div>
        </CollapsibleSection>
      )}

      {error && (
        <div className="modifier-manager-error-state">
          <h4>Save failed</h4>
          <p>{error}</p>
        </div>
      )}

      {canEdit && (
        <div className="modifier-manager-footer">
          <span className="modifier-manager-footer-status">
            {saving ? 'Saving…' : existingRule ? 'Saved to plan' : 'Not yet saved'}
          </span>
          <div className="modifier-manager-footer-actions">
            <button type="button" className="primary-button" onClick={saveRule} disabled={saving || !ruleName}>
              {saving ? 'Saving…' : existingRule ? 'Update rule' : 'Save rule'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// C-67 — ProgramPopulationPanel (inline per OI-S813 pattern)
// ===========================================================================
function ProgramPopulationPanel({ planId, fiscalPeriodId, currentSnapshot, readOnly, onCommitted }) {
  const [mode, setMode]             = useState('by_filter');
  const [filterRoles, setFilterRoles]   = useState([]);
  const [filterSegments, setFilterSegments] = useState([]);
  const [filterBUs, setFilterBUs]       = useState([]);
  const [filterSMs, setFilterSMs]       = useState([]);
  const [posSearch, setPosSearch]       = useState('');
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [posResults, setPosResults]     = useState([]);
  const [posSearching, setPosSearching] = useState(false);
  const [preview, setPreview]           = useState(null);
  const [previewing, setPreviewing]     = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [committing, setCommitting]     = useState(false);
  const [commitError, setCommitError]   = useState(null);
  const [committed, setCommitted]       = useState(false);

  // Org reference data
  const [roles, setRoles]         = useState([]);
  const [segments, setSegments]   = useState([]);
  const [bus, setBus]             = useState([]);
  const [sms, setSms]             = useState([]);
  const [refLoading, setRefLoading] = useState(true);

  // Sortable preview table state
  const [sortField, setSortField] = useState('full_name');
  const [sortDir, setSortDir]     = useState('asc');
  const [previewSearch, setPreviewSearch] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/roles`, { credentials: 'include' }).then(r => r.ok ? r.json() : { roles: [] }),
      fetch(`${API_BASE}/segments`, { credentials: 'include' }).then(r => r.ok ? r.json() : { segments: [] }),
      fetch(`${API_BASE}/business-units`, { credentials: 'include' }).then(r => r.ok ? r.json() : { business_units: [] }),
      fetch(`${API_BASE}/selling-motions`, { credentials: 'include' }).then(r => r.ok ? r.json() : { selling_motions: [] }),
    ]).then(([r, s, b, sm]) => {
      setRoles(r.roles ?? r.items ?? []);
      setSegments(s.segments ?? s.items ?? []);
      setBus(b.business_units ?? b.items ?? []);
      setSms(sm.selling_motions ?? sm.items ?? []);
    }).finally(() => setRefLoading(false));
  }, []);

  // Position search (by_position_list mode)
  useEffect(() => {
    if (mode !== 'by_position_list' || !posSearch || posSearch.length < 2) {
      setPosResults([]); return;
    }
    const t = setTimeout(async () => {
      setPosSearching(true);
      try {
        const r = await fetch(`${API_BASE}/positions?search=${encodeURIComponent(posSearch)}&fiscal_period_id=${fiscalPeriodId}&limit=20`, { credentials: 'include' });
        const d = r.ok ? await r.json() : {};
        setPosResults(d.positions ?? d.items ?? []);
      } finally { setPosSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [posSearch, mode, fiscalPeriodId]);

  function toggleMulti(arr, setArr, val) {
    setArr(a => a.includes(val) ? a.filter(x => x !== val) : [...a, val]);
    setPreview(null);
  }

  function buildBody() {
    return {
      fiscal_period_id: fiscalPeriodId,
      instrument_type:  'spiff',
      assignment_mode:  mode,
      plan_ids:         mode === 'by_plan' ? [planId] : null,
      filters: mode === 'by_filter' ? {
        role_codes:           filterRoles,
        segment_codes:        filterSegments,
        business_unit_ids:    filterBUs.map(Number),
        selling_motion_codes: filterSMs,
      } : null,
      position_ids: mode === 'by_position_list' ? selectedPositions.map(p => p.position_id) : null,
    };
  }

  async function runPreview() {
    setPreviewing(true); setPreviewError(null);
    try {
      const r = await fetch(`${API_BASE}/in-period-incentives/preview-population`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      const d = r.ok ? await r.json() : await r.json().then(b => { throw new Error(b?.error?.message || 'Preview failed'); });
      setPreview(d);
    } catch (err) { setPreviewError(err.message); }
    finally { setPreviewing(false); }
  }

  async function commit() {
    setCommitting(true); setCommitError(null);
    try {
      const r = await fetch(`${API_BASE}/spiff-plans/${planId}/assign-population`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...buildBody(), fiscal_period_id: fiscalPeriodId }),
      });
      const d = r.ok ? await r.json() : await r.json().then(b => { throw new Error(b?.error?.message || 'Commit failed'); });
      setCommitted(true);
      onCommitted?.(d);
    } catch (err) { setCommitError(err.message); }
    finally { setCommitting(false); }
  }

  // Sort + search preview positions
  const previewPositions = (preview?.positions_preview ?? []).filter(p =>
    !previewSearch || p.full_name?.toLowerCase().includes(previewSearch.toLowerCase()) || p.employee_id?.includes(previewSearch)
  ).sort((a, b) => {
    const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  function sortBy(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  if (readOnly && currentSnapshot) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgb(var(--color-primary) / 0.07)', border: '1px solid rgb(var(--color-primary) / 0.2)', fontSize: 13 }}>
          <strong>{currentSnapshot.resolved_position_count?.toLocaleString() ?? '?'} positions locked</strong>
          {currentSnapshot.resolved_at && <span style={{ color: 'rgb(var(--color-text-muted))', marginLeft: 8 }}>· Snapshot: {_iso(currentSnapshot.resolved_at)}</span>}
        </div>
        <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
          Population is locked. CA-2 approves this exact set — no dynamic re-query.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Existing snapshot banner */}
      {currentSnapshot && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
          <span>Existing snapshot: <strong>{currentSnapshot.resolved_position_count?.toLocaleString()} positions</strong> locked {_iso(currentSnapshot.resolved_at)}</span>
          {!readOnly && <span style={{ color: 'rgb(var(--color-text-muted))' }}>Re-committing will replace the existing snapshot.</span>}
        </div>
      )}

      {/* Mode selector */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(var(--color-text))' }}>Assignment mode</span>
        {[
          { key: 'by_plan',           label: 'By plan',         desc: 'All active reps on plans with matching component' },
          { key: 'by_filter',         label: 'By filter',       desc: 'Filter by role, segment, BU, selling motion' },
          { key: 'by_position_list',  label: 'By position list', desc: 'Search and select individual positions' },
        ].map(m => (
          <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, border: `1px solid ${mode === m.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-border))'}`, background: mode === m.key ? 'rgb(var(--color-primary) / 0.05)' : 'transparent' }}>
            <input type="radio" name="assignMode" value={m.key} checked={mode === m.key} onChange={() => { setMode(m.key); setPreview(null); }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>{m.label}</div>
              <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{m.desc}</div>
            </div>
          </label>
        ))}
      </div>

      {/* Filter bar — D-3 minimum four filters */}
      {mode === 'by_filter' && (
        <CollapsibleSection title="Population Filters" defaultOpen>
          {refLoading ? <div className="modifier-manager-loading">Loading filters…</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <Field label="Role">
                  <select multiple size={4} style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: 6, fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
                    value={filterRoles} onChange={e => { setFilterRoles(Array.from(e.target.selectedOptions, o => o.value)); setPreview(null); }}>
                    {roles.map(r => <option key={r.role_code ?? r.id} value={r.role_code ?? r.id}>{r.role_label ?? r.role_code ?? r.name}</option>)}
                  </select>
                </Field>
                <Field label="Segment">
                  <select multiple size={4} style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: 6, fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
                    value={filterSegments} onChange={e => { setFilterSegments(Array.from(e.target.selectedOptions, o => o.value)); setPreview(null); }}>
                    {segments.map(s => <option key={s.segment_code ?? s.id} value={s.segment_code ?? s.id}>{s.segment_label ?? s.segment_code ?? s.name}</option>)}
                  </select>
                </Field>
                <Field label="Business Unit">
                  <select multiple size={4} style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: 6, fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
                    value={filterBUs.map(String)} onChange={e => { setFilterBUs(Array.from(e.target.selectedOptions, o => o.value)); setPreview(null); }}>
                    {bus.map(b => <option key={b.business_unit_id ?? b.id} value={String(b.business_unit_id ?? b.id)}>{b.business_unit_name ?? b.name}</option>)}
                  </select>
                </Field>
                <Field label="Selling Motion">
                  <select multiple size={4} style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: 6, fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
                    value={filterSMs} onChange={e => { setFilterSMs(Array.from(e.target.selectedOptions, o => o.value)); setPreview(null); }}>
                    {sms.map(s => <option key={s.selling_motion_code ?? s.id} value={s.selling_motion_code ?? s.id}>{s.selling_motion_label ?? s.selling_motion_code ?? s.name}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>Hold Ctrl/Cmd to select multiple values in each filter.</div>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Position list mode */}
      {mode === 'by_position_list' && (
        <CollapsibleSection title="Select Positions" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Field label="Search positions">
              <input style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
                value={posSearch} onChange={e => setPosSearch(e.target.value)}
                placeholder="Search by name or employee ID…" />
            </Field>
            {posSearching && <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>Searching…</div>}
            {posResults.length > 0 && (
              <div className="modifier-manager-table-wrapper">
                <table className="modifier-manager-table">
                  <thead><tr><th>Name</th><th>Employee ID</th><th>Role</th><th /></tr></thead>
                  <tbody>
                    {posResults.map(p => (
                      <tr key={p.position_id}>
                        <td>{p.full_name}</td>
                        <td>{p.employee_id}</td>
                        <td>{p.role_code}</td>
                        <td><button type="button" className="text-button" onClick={() => { if (!selectedPositions.find(s => s.position_id === p.position_id)) { setSelectedPositions(a => [...a, p]); setPreview(null); } }}>+ Add</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {selectedPositions.length > 0 && (
              <div className="modifier-manager-table-wrapper">
                <table className="modifier-manager-table">
                  <thead><tr><th>Name</th><th>Employee ID</th><th>Role</th><th /></tr></thead>
                  <tbody>
                    {selectedPositions.map(p => (
                      <tr key={p.position_id}>
                        <td>{p.full_name}</td>
                        <td>{p.employee_id}</td>
                        <td>{p.role_code}</td>
                        <td><button type="button" className="text-button text-button--danger" onClick={() => { setSelectedPositions(a => a.filter(s => s.position_id !== p.position_id)); setPreview(null); }}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Preview */}
      {previewError && <div className="modifier-manager-error-state"><h4>Preview failed</h4><p>{previewError}</p></div>}

      <button type="button" className="secondary-button" onClick={runPreview} disabled={previewing} style={{ alignSelf: 'flex-start' }}>
        {previewing ? 'Previewing…' : '↺ Preview population'}
      </button>

      {preview && (
        <CollapsibleSection
          title={`Preview: ${preview.positions_in_scope.toLocaleString()} positions match`}
          defaultOpen
        >
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <input
                style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
                placeholder="Search preview…"
                value={previewSearch}
                onChange={e => setPreviewSearch(e.target.value)}
              />
              <button type="button" className="text-button" onClick={() => setPreviewExpanded(x => !x)}>
                {previewExpanded ? '▲ Collapse' : `▼ View all ${preview.positions_in_scope} positions`}
              </button>
            </div>
            {previewExpanded && (
              <div className="modifier-manager-table-wrapper">
                <table className="modifier-manager-table">
                  <thead>
                    <tr>
                      {[['full_name','Name'],['employee_id','Employee ID'],['role_code','Role']].map(([f,l]) => (
                        <th key={f} onClick={() => sortBy(f)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          {l}<SortIcon field={f} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewPositions.map(p => (
                      <tr key={p.position_id}>
                        <td>{p.full_name}</td>
                        <td>{p.employee_id}</td>
                        <td>{p.role_code}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Commit */}
      {commitError && <div className="modifier-manager-error-state"><h4>Commit failed</h4><p>{commitError}</p></div>}
      {committed && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-success) / 0.08)', border: '1px solid rgb(var(--color-success) / 0.25)', fontSize: 13, color: 'rgb(var(--color-success))' }}>
          ✓ Population committed — snapshot locked.
        </div>
      )}

      {!readOnly && preview && preview.positions_in_scope > 0 && !committed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
            Committing will lock {preview.positions_in_scope.toLocaleString()} positions at this timestamp. CA-2 approves this exact set — no dynamic re-query.
          </div>
          <button type="button" className="primary-button" onClick={commit} disabled={committing} style={{ alignSelf: 'flex-start' }}>
            {committing ? 'Committing…' : `Commit population assignment (${preview.positions_in_scope.toLocaleString()} positions)`}
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 4 — AWARDS
// ===========================================================================
function AwardsTab({ program, planId, readOnly }) {
  const [awards, setAwards]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [sortField, setSortField] = useState('awarded_at');
  const [sortDir, setSortDir]     = useState('desc');
  const [page, setPage]           = useState(0);
  const PAGE_SIZE = 50;

  // New award form (manager_assigned only)
  const [repSearch, setRepSearch]   = useState('');
  const [repResults, setRepResults] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [awardAmount, setAwardAmount] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [submittingAward, setSubmittingAward] = useState(false);
  const [awardError, setAwardError] = useState(null);

  const isManagerAssigned = program?.triggerModel === 'manager_assigned';

  useEffect(() => {
    if (!planId) return;
    setLoading(true);
    fetch(`${API_BASE}/spiff-awards?plan_id=${planId}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { spiff_awards: [], total: 0 })
      .then(d => { setAwards(d.spiff_awards ?? []); setTotal(d.total ?? 0); })
      .finally(() => setLoading(false));
  }, [planId, page]);

  useEffect(() => {
    if (!repSearch || repSearch.length < 2 || !planId) { setRepResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API_BASE}/spiff-plans/${planId}/assignments?is_active=true&limit=20`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : { assignments: [] })
        .then(d => setRepResults((d.assignments ?? []).filter(a =>
          a.full_name?.toLowerCase().includes(repSearch.toLowerCase()) || a.employee_id?.includes(repSearch)
        )));
    }, 300);
    return () => clearTimeout(t);
  }, [repSearch, planId]);

  async function addAward() {
    if (!selectedRep || !awardAmount) return;
    setSubmittingAward(true); setAwardError(null);
    try {
      const r = await fetch(`${API_BASE}/spiff-awards`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          position_id: selectedRep.position_id,
          fiscal_period_id: program?.fiscalPeriodId ?? selectedRep.fiscal_period_id,
          award_amount: awardAmount,
          award_currency: program?.payoutCurrency ?? 'USD',
          award_reason: awardReason || null,
        }),
      });
      if (!r.ok) { const b = await r.json(); throw new Error(b?.error?.message || 'Failed'); }
      setSelectedRep(null); setAwardAmount(''); setAwardReason(''); setRepSearch('');
      setPage(0); // reload
    } catch (err) { setAwardError(err.message); }
    finally { setSubmittingAward(false); }
  }

  function sortBy(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const filteredAwards = awards.filter(a =>
    !search || a.position_id?.includes(search) || String(a.award_amount).includes(search)
  ).sort((a, b) => {
    const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const hasMore = (page + 1) * PAGE_SIZE < total;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Transaction triggered: info panel */}
      {!isManagerAssigned && (
        <CollapsibleSection title="File Upload" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>
              Transaction-triggered awards are calculated by the engine from credit data.
              To load manual override awards, use <strong>EXECUTE → Data Load → SPIFF Awards</strong>.
            </div>
            <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
              Note: <code>spiff_award</code> import type registration pending (AD-S923-02). File upload available once backend pipeline is complete.
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Manager assigned: manual award entry */}
      {isManagerAssigned && !readOnly && (
        <CollapsibleSection title="Add Award" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Search rep (assigned positions only)">
              <input style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
                value={repSearch} onChange={e => { setRepSearch(e.target.value); setSelectedRep(null); }}
                placeholder="Search by name or employee ID…" />
            </Field>
            {repResults.length > 0 && !selectedRep && (
              <div className="modifier-manager-table-wrapper">
                <table className="modifier-manager-table">
                  <thead><tr><th>Name</th><th>Employee ID</th><th>Role</th><th /></tr></thead>
                  <tbody>
                    {repResults.map(r => (
                      <tr key={r.position_id} onClick={() => { setSelectedRep(r); setRepSearch(r.full_name); setRepResults([]); }} style={{ cursor: 'pointer' }}>
                        <td>{r.full_name}</td><td>{r.employee_id}</td><td>{r.role_code}</td>
                        <td><button type="button" className="text-button">Select</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {selectedRep && (
              <div style={{ padding: '6px 10px', borderRadius: 6, background: 'rgb(var(--color-primary) / 0.07)', border: '1px solid rgb(var(--color-primary) / 0.2)', fontSize: 12 }}>
                Selected: <strong>{selectedRep.full_name}</strong> · {selectedRep.employee_id}
                <button type="button" className="text-button" style={{ marginLeft: 8 }} onClick={() => { setSelectedRep(null); setRepSearch(''); }}>✕</button>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
              <Field label="Award amount *">
                <input type="number" min="0" step="0.01"
                  style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
                  value={awardAmount} onChange={e => setAwardAmount(e.target.value)}
                  placeholder={program?.payoutCurrency ?? 'USD'} />
              </Field>
              <Field label="Award reason (optional)">
                <input style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
                  value={awardReason} onChange={e => setAwardReason(e.target.value)}
                  placeholder="e.g. Exceeded Q3 target" />
              </Field>
            </div>
            {program?.maxIndividualAward && awardAmount && parseFloat(awardAmount) > parseFloat(program.maxIndividualAward) && (
              <div style={{ fontSize: 12, color: 'rgb(var(--color-warning))' }}>
                ⚠ Award exceeds policy envelope max ({_fmt(program.maxIndividualAward, program.payoutCurrency)}).
              </div>
            )}
            {awardError && <div style={{ fontSize: 12, color: 'rgb(var(--color-danger))' }}>{awardError}</div>}
            <button type="button" className="primary-button" onClick={addAward}
              disabled={submittingAward || !selectedRep || !awardAmount} style={{ alignSelf: 'flex-start' }}>
              {submittingAward ? 'Adding…' : '+ Add award'}
            </button>
          </div>
        </CollapsibleSection>
      )}

      {/* Awards list — scrollable, sortable, searchable, clickable (D-1) */}
      <CollapsibleSection title={`Awards (${total.toLocaleString()})`} defaultOpen>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <input style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
            placeholder="Search awards…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {loading ? (
          <div className="modifier-manager-loading">Loading awards…</div>
        ) : filteredAwards.length === 0 ? (
          <div className="modifier-manager-empty-state"><h4>No awards</h4><p>No awards have been created for this program yet.</p></div>
        ) : (
          <div className="modifier-manager-table-wrapper">
            <table className="modifier-manager-table">
              <thead>
                <tr>
                  {[['position_id','Position'],['award_amount','Amount'],['award_status','Status'],['awarded_at','Awarded']].map(([f,l]) => (
                    <th key={f} onClick={() => sortBy(f)} style={{ cursor: 'pointer', userSelect: 'none' }}>{l}<SortIcon field={f} /></th>
                  ))}
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredAwards.map(a => (
                  <tr key={a.award_id} style={{ cursor: 'pointer' }}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{String(a.position_id).slice(0, 8)}…</td>
                    <td><strong>{_fmt(a.award_amount, a.award_currency)}</strong></td>
                    <td><span className={`modifier-manager-badge ${a.award_status === 'approved' ? 'modifier-manager-badge--complete' : a.award_status === 'pending_approval' ? 'modifier-manager-badge--warning' : 'modifier-manager-badge--not-created'}`}>{a.award_status}</span></td>
                    <td style={{ fontSize: 11 }}>{_iso(a.awarded_at)}</td>
                    <td style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{a.award_reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(page > 0 || hasMore) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
            <button type="button" className="secondary-button" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Previous</button>
            <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>Page {page + 1}</span>
            <button type="button" className="secondary-button" disabled={!hasMore} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
}

// ===========================================================================
// C-68 — ProgramApprovalPanel (inline)
// ===========================================================================
function ProgramApprovalPanel({ program, planId, currentUser, onActionComplete }) {
  const { submit, approve, reject, withdraw, saving, saveError } = useSpiffProgram(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const status = program?.planStatus ?? 'draft';
  const submittedBy = program?.submittedBy;
  const approvedBy  = program?.approvedBy;
  const myId        = currentUser?.user_account_id ?? currentUser?.user_id;
  const isSelf      = myId && submittedBy && String(myId) === String(submittedBy);
  const isSubmitter = myId && submittedBy && String(myId) === String(submittedBy);

  async function doAction(fn) {
    try { await fn(); onActionComplete?.(); } catch { /* saveError shown below */ }
  }

  // Checklist for draft state
  const hasRules = (program?.rules?.length ?? 0) > 0 || program?.triggerModel === 'manager_assigned';
  const hasPopulation = (program?.activeAssignmentCount ?? 0) > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Status header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>Approval Status</span>
        <span className={`modifier-manager-badge ${STATUS_BADGE[status] ?? 'modifier-manager-badge--not-created'}`}>
          {STATUS_LABEL[status] ?? status}
        </span>
      </div>

      {saveError && (
        <div className="modifier-manager-error-state"><h4>Action failed</h4><p>{saveError}</p></div>
      )}

      {/* Draft state */}
      {status === 'draft' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>
            Complete the Rules and Population tabs before submitting for approval.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: hasRules ? 'rgb(var(--color-success))' : 'rgb(var(--color-text-muted))' }}>{hasRules ? '✓' : '○'}</span>
              <span>Rules defined</span>
            </div>
            <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: hasPopulation ? 'rgb(var(--color-success))' : 'rgb(var(--color-text-muted))' }}>{hasPopulation ? '✓' : '○'}</span>
              <span>Population assigned ({program?.activeAssignmentCount ?? 0} positions)</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" className="primary-button"
              onClick={() => doAction(() => submit())}
              disabled={saving || !planId}>
              {saving ? 'Submitting…' : 'Submit for approval'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
            SoD: You will not be able to approve your own submission.
          </div>
        </div>
      )}

      {/* Pending approval state */}
      {status === 'pending_approval' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <InfoRow label="Submitted by" value={program.submittedBy} />
            <InfoRow label="Submitted at" value={_iso(program.submittedAt)} />
          </div>

          {isSelf ? (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12 }}>
              You submitted this program and cannot approve it (SoD requirement). A different comp admin must approve.
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="button" className="primary-button"
              onClick={() => doAction(() => approve())}
              disabled={saving || isSelf || !planId}
              title={isSelf ? 'You submitted this program — a different admin must approve' : ''}>
              {saving ? 'Approving…' : 'Approve'}
            </button>
            {!showRejectForm && (
              <button type="button" className="secondary-button"
                onClick={() => setShowRejectForm(true)} disabled={saving || isSubmitter}>
                Reject with note
              </button>
            )}
            {isSubmitter && (
              <button type="button" className="text-button"
                onClick={() => doAction(() => withdraw())} disabled={saving}>
                Withdraw submission
              </button>
            )}
          </div>

          {showRejectForm && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label="Rejection note">
                <textarea rows={3}
                  style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                  value={rejectNote} onChange={e => setRejectNote(e.target.value)}
                  placeholder="Reason for rejection…" />
              </Field>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="primary-button"
                  onClick={() => doAction(() => reject())} disabled={saving}>
                  {saving ? 'Rejecting…' : 'Confirm rejection'}
                </button>
                <button type="button" className="secondary-button" onClick={() => setShowRejectForm(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Approved state */}
      {(status === 'approved' || status === 'active') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Submitted by" value={program.submittedBy} />
          <InfoRow label="Submitted at" value={_iso(program.submittedAt)} />
          <InfoRow label="Approved by"  value={program.approvedBy} />
          <InfoRow label="Approved at"  value={_iso(program.approvedAt)} />
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-success) / 0.07)', border: '1px solid rgb(var(--color-success) / 0.2)', fontSize: 12, color: 'rgb(var(--color-success))' }}>
            ✓ Engine gate G-33 SATISFIED — approved_by IS NOT NULL. Next calculation run will include this program.
          </div>
        </div>
      )}

      {status === 'superseded' && (
        <div className="modifier-manager-readonly-note">
          This plan version has been superseded. Use the current active version.
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// C-69 — ProgramActivityPanel (inline)
// ===========================================================================
function ProgramActivityPanel({ planId, entityLabel }) {
  const [entries, setEntries]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [hasMore, setHasMore]   = useState(false);
  const [page, setPage]         = useState(0);
  const [search, setSearch]     = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const PAGE_SIZE = 25;

  useEffect(() => {
    if (!planId) return;
    setLoading(true);
    const params = new URLSearchParams({
      entity_type: 'spiff_plan',
      entity_id_int: String(planId),
      limit: String(PAGE_SIZE),
      offset: String(page * PAGE_SIZE),
    });
    if (search) params.set('search', search);
    fetch(`${API_BASE}/activity/audit-trail?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { entries: [], totalCount: 0 })
      .then(d => {
        setEntries(d.entries ?? d.items ?? []);
        setTotal(d.totalCount ?? d.total ?? 0);
        setHasMore((page + 1) * PAGE_SIZE < (d.totalCount ?? d.total ?? 0));
      })
      .finally(() => setLoading(false));
  }, [planId, page, search, refreshKey]);

  useEffect(() => {
    const t = setTimeout(() => setPage(0), 300);
    return () => clearTimeout(t);
  }, [search]);

  function eventLabel(action) {
    const map = {
      'spiff_plan.created':              'Program created (Draft)',
      'spiff_plan.updated':              'Program updated',
      'spiff_plan.submitted':            'Program submitted for approval',
      'spiff_plan.approved':             'Program approved (CA-2) — G-33 satisfied',
      'spiff_plan.rejected':             'Program returned for changes',
      'spiff.plan.withdraw_submission':  'Submission withdrawn',
      'spiff_plan.superseded':           'Plan version superseded',
      'spiff_rule.created':              'Rule added',
      'spiff_plan.population_committed': 'Population committed — snapshot locked',
      'spiff_plan.finance_approved':     'Finance approval recorded',
    };
    return map[action] ?? action;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>
          Activity — {entityLabel}
        </span>
        <button type="button" className="icon-button" onClick={() => { setRefreshKey(k => k + 1); setPage(0); }} title="Refresh" aria-label="Refresh activity">↻</button>
      </div>

      <input
        style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
        placeholder="Search activity…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {loading && entries.length === 0 && (
        <div className="modifier-manager-loading">Loading activity…</div>
      )}

      {!loading && entries.length === 0 && (
        <div className="modifier-manager-empty-state">
          <h4>No activity yet</h4>
          <p>Activity will appear here as actions are taken on this program.</p>
        </div>
      )}

      {entries.map((e, i) => (
        <div key={e.id ?? i} style={{
          display: 'flex', gap: 12, padding: '10px 0',
          borderBottom: '1px solid rgb(var(--color-border))',
        }}>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', minWidth: 140, flexShrink: 0 }}>
            {_iso(e.created_at)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{eventLabel(e.action ?? e.event_type)}</span>
            {e.actor_email && (
              <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{e.actor_email}</span>
            )}
            {e.metadata && Object.keys(e.metadata).length > 0 && (
              <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
                {JSON.stringify(e.metadata)}
              </span>
            )}
          </div>
        </div>
      ))}

      {(page > 0 || hasMore) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
          <button type="button" className="secondary-button" disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>← Previous</button>
          <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{entries.length} of {total.toLocaleString()}</span>
          <button type="button" className="secondary-button" disabled={!hasMore || loading} onClick={() => setPage(p => p + 1)}>Load more</button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// MAIN — SpiffProgramWorkspace (C-66)
// ===========================================================================
export default function SpiffProgramWorkspace({ planId: initialPlanId, fiscalPeriodId, currentUser, onClose, onSaved }) {
  const [activeTab, setActiveTab]   = useState('overview');
  const [planId, setPlanId]         = useState(initialPlanId ?? null);
  const isCreate = !planId;

  const { program, loading, error, saving, saveError, load, create, patch } = useSpiffProgram(planId);

  // Create form state (only used when planId is null)
  const [createForm, setCreateForm] = useState({
    plan_name: '', spiff_plan_code: '', trigger_model: '', plan_component_id: '',
    effective_from: '', effective_to: '', payout_currency: 'USD', payout_cap: '',
    max_individual_award: '', max_duration_days: '', finance_approval_threshold: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  async function handleCreate() {
    setCreating(true); setCreateError(null);
    try {
      const body = {
        plan_name:                  createForm.plan_name,
        spiff_plan_code:            createForm.spiff_plan_code || null,
        trigger_model:              createForm.trigger_model,
        plan_component_id:          parseInt(createForm.plan_component_id, 10),
        effective_from:             createForm.effective_from,
        effective_to:               createForm.effective_to || null,
        payout_currency:            createForm.payout_currency,
        payout_cap:                 createForm.payout_cap || null,
        max_individual_award:       createForm.max_individual_award || null,
        max_duration_days:          createForm.max_duration_days ? parseInt(createForm.max_duration_days, 10) : null,
        finance_approval_threshold: createForm.finance_approval_threshold || null,
      };
      const plan = await create(body);
      if (plan?.plan_id) {
        setPlanId(plan.plan_id);
        setActiveTab('rules');
        onSaved?.();
      }
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  const readOnly = program?.planStatus !== 'draft';
  const statusBadgeCls = STATUS_BADGE[program?.planStatus] ?? 'modifier-manager-badge--not-created';

  // ---------------------------------------------------------------------------
  // Create form
  // ---------------------------------------------------------------------------
  if (isCreate) {
    const S = {
      input: { border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' },
      select: { border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' },
      grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', marginBottom: 4, margin: 0 }}>
              EXECUTE · Programs &amp; Adjustments
            </p>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: '4px 0 0' }}>New SPIFF Program</h1>
          </div>
          <button type="button" className="secondary-button" onClick={onClose}>← Back</button>
        </div>

        <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={S.grid2}>
            <Field label="Program name" required>
              <input style={S.input} value={createForm.plan_name} onChange={e => setCreateForm(f => ({ ...f, plan_name: e.target.value }))} placeholder="e.g. Q3 Cloud Upsell SPIFF" />
            </Field>
            <Field label="Program code" hint="Auto-generated if blank">
              <input style={S.input} value={createForm.spiff_plan_code} onChange={e => setCreateForm(f => ({ ...f, spiff_plan_code: e.target.value.toUpperCase() }))} placeholder="e.g. Q3_CLOUD_UPSELL" maxLength={120} />
            </Field>
          </div>
          <div style={S.grid2}>
            <Field label="Trigger model" required>
              <select style={S.select} value={createForm.trigger_model} onChange={e => setCreateForm(f => ({ ...f, trigger_model: e.target.value }))}>
                <option value="">Select trigger model</option>
                <option value="transaction_triggered">Transaction Triggered</option>
                <option value="manager_assigned">Manager Assigned</option>
              </select>
            </Field>
            <Field label="Target plan component ID" required hint="Links this program to the governing plan component">
              <input type="number" style={S.input} value={createForm.plan_component_id} onChange={e => setCreateForm(f => ({ ...f, plan_component_id: e.target.value }))} placeholder="e.g. 70178" />
            </Field>
          </div>
          <div style={S.grid2}>
            <Field label="Effective from" required>
              <input type="date" style={S.input} value={createForm.effective_from} onChange={e => setCreateForm(f => ({ ...f, effective_from: e.target.value }))} />
            </Field>
            <Field label="Effective to" hint="Leave blank for open-ended">
              <input type="date" style={S.input} value={createForm.effective_to} onChange={e => setCreateForm(f => ({ ...f, effective_to: e.target.value }))} />
            </Field>
          </div>
          <div style={S.grid2}>
            <Field label="Payout currency" required>
              <input style={S.input} value={createForm.payout_currency} onChange={e => setCreateForm(f => ({ ...f, payout_currency: e.target.value.toUpperCase() }))} maxLength={3} placeholder="USD" />
            </Field>
            <Field label="Payout cap" hint="Maximum total payout for this program (optional)">
              <input type="number" style={S.input} value={createForm.payout_cap} onChange={e => setCreateForm(f => ({ ...f, payout_cap: e.target.value }))} placeholder="Optional" min="0" />
            </Field>
          </div>

          {/* Policy envelope — collapsible */}
          <CollapsibleSection title="Policy Envelope (Optional)" defaultOpen={false}>
            <div style={S.grid2}>
              <Field label="Max individual award" hint="Maximum single award per rep">
                <input type="number" style={S.input} value={createForm.max_individual_award} onChange={e => setCreateForm(f => ({ ...f, max_individual_award: e.target.value }))} placeholder="Optional" min="0" />
              </Field>
              <Field label="Max duration (days)" hint="Maximum program length">
                <input type="number" style={S.input} value={createForm.max_duration_days} onChange={e => setCreateForm(f => ({ ...f, max_duration_days: e.target.value }))} placeholder="Optional" min="1" />
              </Field>
              <Field label="Finance gate threshold" hint="Total exposure above which Finance approval required">
                <input type="number" style={S.input} value={createForm.finance_approval_threshold} onChange={e => setCreateForm(f => ({ ...f, finance_approval_threshold: e.target.value }))} placeholder="Optional" min="0" />
              </Field>
            </div>
          </CollapsibleSection>

          {createError && <div className="modifier-manager-error-state"><h4>Failed to create</h4><p>{createError}</p></div>}

          <div className="modifier-manager-footer">
            <span className="modifier-manager-footer-status" />
            <div className="modifier-manager-footer-actions">
              <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
              <button type="button" className="primary-button"
                onClick={handleCreate}
                disabled={creating || !createForm.plan_name || !createForm.trigger_model || !createForm.plan_component_id || !createForm.effective_from}>
                {creating ? 'Creating…' : 'Create program'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Workspace (existing program)
  // ---------------------------------------------------------------------------
  if (loading && !program) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="skeleton" style={{ height: 24, width: 300, margin: '0 auto 12px' }} />
        <div className="skeleton" style={{ height: 16, width: 200, margin: '0 auto' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="modifier-manager-error-state" style={{ margin: 20 }}>
        <h4>Failed to load program</h4>
        <p>{error}</p>
        <button type="button" className="secondary-button" onClick={() => load(planId)}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        padding: '16px 0 12px', borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 0,
      }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
            EXECUTE · Programs &amp; Adjustments · SPIFF
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: 0 }}>
              {program?.planName ?? 'Loading…'}
            </h1>
            {program?.spiffPlanCode && (
              <code className="modifier-manager-code">{program.spiffPlanCode}</code>
            )}
            <span className={`modifier-manager-badge ${statusBadgeCls}`}>
              {STATUS_LABEL[program?.planStatus] ?? program?.planStatus}
            </span>
          </div>
          {program?.triggerModel && (
            <p style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', margin: '4px 0 0' }}>
              {TRIGGER_LABELS[program.triggerModel]} · {program.effectiveFrom} → {program.effectiveTo ?? 'Open ended'} · {program.payoutCurrency}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="icon-button" onClick={() => load(planId)} title="Refresh" aria-label="Refresh program">↻</button>
          <button type="button" className="secondary-button" onClick={onClose}>← Back</button>
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid rgb(var(--color-border))',
        marginBottom: 20,
      }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-text-muted))',
              background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '2px solid rgb(var(--color-primary))' : '2px solid transparent',
              cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
              marginBottom: -1,
            }}
          >
            {tab.label}
            {/* Pending attention indicators */}
            {tab.key === 'approval' && program?.planStatus === 'pending_approval' && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, background: 'rgb(var(--color-warning) / 0.15)', color: 'rgb(var(--color-warning))' }}>!</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ────────────────────────────────────────────────── */}
      <div style={{ minHeight: 400 }}>
        {activeTab === 'overview' && (
          <OverviewTab program={program} />
        )}

        {activeTab === 'rules' && (
          <RulesTab
            program={program}
            planId={planId}
            readOnly={readOnly}
            onRulesChanged={() => load(planId)}
          />
        )}

        {activeTab === 'population' && (
          <ProgramPopulationPanel
            planId={planId}
            fiscalPeriodId={fiscalPeriodId}
            currentSnapshot={program?.activeAssignmentCount > 0 ? {
              resolved_position_count: program.activeAssignmentCount,
            } : null}
            readOnly={readOnly}
            onCommitted={() => { load(planId); onSaved?.(); }}
          />
        )}

        {activeTab === 'awards' && (
          <AwardsTab
            program={program}
            planId={planId}
            readOnly={false}
          />
        )}

        {activeTab === 'approval' && (
          <ProgramApprovalPanel
            program={program}
            planId={planId}
            currentUser={currentUser}
            onActionComplete={() => { load(planId); onSaved?.(); }}
          />
        )}

        {activeTab === 'activity' && (
          <ProgramActivityPanel
            planId={planId}
            entityLabel={program?.planName ?? 'Program'}
          />
        )}
      </div>
    </div>
  );
}
